import { computed } from 'vue';
import { useStore, useRoute } from '@/utils/vue-api';
import { miniSafeGet } from '@/utils/api';
import i18n, { ZH, ZH_HK, EN } from '@/locales';
import { MOBILE_URL } from '@/utils/host';
import { resolveStoreId } from '@rebuild/utils/context';
import { getCookie } from '@/utils/cookie';
import { getDefaultThemeColor } from '@/store/modules/theme';

export const useCommonData = () => {
	const route = useRoute();
	const store = useStore();

	const getOrganizationName = computed(() => store.getters.getOrganizationName);
	const getFullCode = computed(() => {
		const { code, market } = store.getters.getBaseData;
		return `${market?.toLowerCase()}${code}`;
	});
	const getSlogan = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'slogan',
			[ZH_HK]: 'sloganZhCht',
			[EN]: 'sloganEn'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey);
	});
	const getIndustry = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'industry.name',
			[ZH_HK]: 'industryZhCht',
			[EN]: 'industryEn'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey);
	});
	const getAddressList = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'addressList',
			[ZH_HK]: 'addressZhChtList',
			[EN]: 'addressEnList'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey, []);
	});
	const getPhone = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'phone',
			[ZH_HK]: 'phoneZhCht',
			[EN]: 'phoneEn'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey, '');
	});
	const getEmail = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'email',
			[ZH_HK]: 'emailZhCht',
			[EN]: 'emailEn'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey, '');
	});
	const getChannelList = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'otherChannelList',
			[ZH_HK]: 'otherChannelZhChtList',
			[EN]: 'otherChannelEnList'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey, []);
	});
	const getIntroduction = computed(() => {
		const baseData = getColumnData.value;
		const keyMap = {
			[ZH]: 'introduction',
			[ZH_HK]: 'introductionZhCht',
			[EN]: 'introductionEn'
		};
		const findKey = keyMap[i18n.locale];
		return miniSafeGet(baseData, findKey);
	});

	// 主题色统一转大写
	const getUpperThemeColor = computed(() => {
		const defaultColor = getDefaultThemeColor();
		let themeColor = store.getters.getCommonData?.themeColorsHex || defaultColor;
		themeColor = themeColor.toLocaleUpperCase();
		return themeColor;
	});
	const isDarkTheme = computed(() => getUpperThemeColor.value === '#000000');

	const getBackgroundImage = computed(() => store.getters.getCommonData?.backgroundImage);
	const getImagePosition = computed(() => store.getters.getCommonData?.imagePosition);

	const getFeedbackEntry = computed(() =>
		store.getters.getGlobalModules.some(item => item.moduleId === 'leave_word')
	);
	const getSurveyEntry = computed(() => store.getters.getGlobalModules.some(item => item.moduleId === 'appointment'));
	const getSubscription = computed(() =>
		store.getters.getGlobalModules.some(item => item.moduleId === 'message_subscription')
	);

	const getColumnData = computed(() => store.getters.getBaseData || {});
	const getStockInfoVOS = computed(() => store.getters.getCommonData.stockInfoVOS);

	// TODO 部分保留
	const isLAOPU = computed(() => {
		// 老铺黄金
		const storeId = resolveStoreId();
		return storeId == 22808;
	});
	const COLOR_LAOPU = '#D2A36C';
	const isHONTRON = computed(() => {
		// 宏创控股
		const baseData = getColumnData.value;
		const stockCode = baseData.code;
		return stockCode == '002379';
	});

	/**
	 * 生成移动端模块链接
	 * @param {string} moduleId - 模块ID，如 'message_subscription', 'appointment', 'leave_word' 等
	 * @param {object} extraParams - 可选的额外参数对象，如 { f: 'PC' }
	 * @returns {string} 完整的移动端链接
	 */
	const generateMobileModuleUrl = (moduleId, extraParams = {}) => {
		// 基础URL组成
		const baseUrl = `${MOBILE_URL}/irm-mobile/enterprise/${moduleId}`;

		const baseParams = {
			storeId: resolveStoreId(),
			pageType: store.getters.getIrPageType,
			lang: i18n.locale
		};

		if (isSlideMTmpl.value) {
			baseParams.tmplId = route.params?.tmplId;
		}

		const releaseInCookie = getCookie('release');
		if (releaseInCookie) {
			baseParams.release = releaseInCookie;
		}

		const modeInUrl = route.query.mode;
		if (modeInUrl) {
			baseParams.mode = modeInUrl;
		}

		// 基础参数
		const params = new URLSearchParams({
			...baseParams
		});

		// 添加额外参数
		if (extraParams && typeof extraParams === 'object') {
			Object.keys(extraParams).forEach(key => {
				params.set(key, extraParams[key]);
			});
		}

		return `${baseUrl}?${params.toString()}`;
	};

	/**
	 * mode
	 * - link 复制链接
	 * - preview 预览
	 * - iframe 实时预览窗口
	 */
	const viewInPreview = computed(() => {
		const { mode = '' } = route.query;
		return mode === 'preview';
	});
	const viewInIframe = computed(() => {
		const { mode = '' } = route.query;
		return mode === 'iframe';
	});

	const isSlideTmpl = computed(() => [4, 6].includes(store.getters.getIrPageType));
	const isSlideMTmpl = computed(() => store.getters.getIrPageType == 6);
	const isFlowXTmpl = computed(() => store.getters.getIrPageType == 5);
	const isFlowTmpl = computed(() => store.getters.getIrPageType == 1);
	const isSurfTmpl = computed(() => store.getters.getIrPageType == 8);
	const isSingleSlideTmpl = computed(() => store.getters.getIrPageType == 4);

	// 仅从语言版本或 pageType 去匹配当前的模板数据，都是不完整的
	// 都不能真正的区分出到底是单页面模板还是多页面模板
	const multiTemplates = [6, 7, 8];

	const allModules = computed(() => store.getters.getPageAllModules);
	const getModuleName = moduleId => {
		const module = allModules.value.find(m => m.moduleId === moduleId);
		return module?.name || '';
	};

	return {
		getOrganizationName,
		getFullCode,
		getSlogan,
		getIndustry,
		getAddressList,
		getPhone,
		getEmail,
		getChannelList,
		getIntroduction,

		getUpperThemeColor,
		isDarkTheme,

		getBackgroundImage,
		getImagePosition,
		getColumnData,
		getStockInfoVOS,
		getFeedbackEntry,
		getSurveyEntry,
		getSubscription,

		isLAOPU,
		COLOR_LAOPU,
		isHONTRON,

		generateMobileModuleUrl,
		getModuleName,

		viewInPreview,
		viewInIframe,

		isSlideTmpl,
		isFlowXTmpl,
		isFlowTmpl,
		isSurfTmpl,
		isSlideMTmpl,
		isSingleSlideTmpl,
		multiTemplates
	};
};
